import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import jsonExport from "jsonexport/dist";
import {
  CanAccess,
  downloadCSV,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { MobileBackButton } from "../misc/MobileBackButton";
import {
  buildMetaCampaignReportCsvRows,
  META_REPORT_EXPORT_DENIED_MESSAGE,
} from "../providers/commons/metaCampaignReportExport";
import type { CrmDataProvider } from "../providers/types";
import type {
  AppRole,
  MetaCampaignReportRow,
  MetaPlatform,
  MetaReportGroupBy,
} from "../types";
import {
  REPORT_GROUPINGS,
  REPORT_PLATFORMS,
  defaultReportRange,
  filterByPlatform,
  formatFirstResponse,
  formatRate,
  formatSaleValue,
  groupingLabelKey,
  isValidReportRange,
  rowLabel,
  withTranslatedHeaders,
} from "./campaignReportRules";

const QUERY_KEY = "meta_campaign_report";
const REPORT_STALE_MS = 60_000;
const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

/**
 * Almikia Epic EM (TASK-068): "Reportes por campaña", direccion and lead_manager only. Leads and
 * their funnel by campaign, ad set or ad for a date range and platform, with a CSV export behind
 * the existing export gate. Never shows an advisor name or ranks advisors. RLS and the report RPC
 * are the real authority; `CanAccess` only sends the other roles home.
 */
export const CampaignReport = () => (
  <CanAccess
    resource="meta_campaign_report"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <CampaignReportContent />
  </CanAccess>
);

CampaignReport.path = "/meta_campaign_report";

const FIGURES: {
  key: string;
  value: (row: MetaCampaignReportRow) => string;
}[] = [
  { key: "leads", value: (row) => String(row.leads) },
  { key: "qualified_leads", value: (row) => String(row.qualified_leads) },
  { key: "appointments", value: (row) => String(row.appointments) },
  { key: "reservations", value: (row) => String(row.reservations) },
  { key: "sales", value: (row) => String(row.sales) },
  { key: "sale_value", value: (row) => formatSaleValue(row.sale_value) },
  { key: "qualified_rate", value: (row) => formatRate(row.qualified_rate) },
  {
    key: "appointment_rate",
    value: (row) => formatRate(row.appointment_rate),
  },
  { key: "sales_rate", value: (row) => formatRate(row.sales_rate) },
  {
    key: "median_first_response",
    value: (row) => formatFirstResponse(row.median_first_response_seconds),
  },
];

const CampaignReportContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const [range, setRange] = useState(() => defaultReportRange());
  const [groupBy, setGroupBy] = useState<MetaReportGroupBy>("campaign");
  const [platform, setPlatform] = useState<MetaPlatform | "">("");
  const rangeIsValid = isValidReportRange(range.from, range.to);

  const report = useQuery({
    queryKey: [QUERY_KEY, range.from, range.to, groupBy],
    enabled: rangeIsValid,
    staleTime: REPORT_STALE_MS,
    placeholderData: keepPreviousData,
    queryFn: () =>
      dataProvider.getMetaCampaignReport({
        from: range.from,
        to: range.to,
        groupBy,
      }),
  });
  const rows = useMemo(
    () => filterByPlatform(report.data ?? [], platform),
    [report.data, platform],
  );

  const exportCsv = () => {
    try {
      const csvRows = withTranslatedHeaders(
        buildMetaCampaignReportCsvRows(
          { role: identity?.role as AppRole, id: identity?.id },
          rows,
        ),
        translate,
      );
      jsonExport(csvRows, {}, (error: unknown, csv: string) => {
        if (error) {
          console.error("campaignReport.export.error", error);
          notify("crm.metaOrigin.report.error", { type: "error" });
          return;
        }
        downloadCSV(csv, "reporte-campanas-meta");
      });
    } catch (error) {
      notify(
        error instanceof Error &&
          error.message === META_REPORT_EXPORT_DENIED_MESSAGE
          ? "crm.metaOrigin.report.export_denied"
          : "crm.metaOrigin.report.error",
        { type: "error" },
      );
    }
  };

  const title = translate("crm.metaOrigin.report.title");
  const content = (
    <>
      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="meta-report-from">
              {translate("crm.metaOrigin.report.range_from")}
            </Label>
            <Input
              id="meta-report-from"
              type="date"
              className="h-11 sm:h-9"
              value={range.from}
              onChange={(event) =>
                setRange((current) => ({
                  ...current,
                  from: event.target.value,
                }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="meta-report-to">
              {translate("crm.metaOrigin.report.range_to")}
            </Label>
            <Input
              id="meta-report-to"
              type="date"
              className="h-11 sm:h-9"
              value={range.to}
              onChange={(event) =>
                setRange((current) => ({ ...current, to: event.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="meta-report-platform">
              {translate("crm.metaOrigin.report.platform_filter")}
            </Label>
            <select
              id="meta-report-platform"
              className={SELECT_CLASS}
              value={platform}
              onChange={(event) =>
                setPlatform(event.target.value as MetaPlatform | "")
              }
            >
              <option value="">
                {translate("crm.metaOrigin.report.platform_all")}
              </option>
              {REPORT_PLATFORMS.map((value) => (
                <option key={value} value={value}>
                  {translate(`crm.metaOrigin.platform.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <span className="text-sm font-medium">
              {translate("crm.metaOrigin.report.group_by")}
            </span>
            <div
              role="group"
              aria-label={translate("crm.metaOrigin.report.group_by")}
              className="flex gap-1"
            >
              {REPORT_GROUPINGS.map((value) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={groupBy === value ? "default" : "outline"}
                  aria-pressed={groupBy === value}
                  className="h-11 flex-1 px-2 sm:h-9"
                  onClick={() => setGroupBy(value)}
                >
                  {translate(groupingLabelKey(value))}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          disabled={rows.length === 0}
          onClick={exportCsv}
        >
          {translate("crm.metaOrigin.report.export_action")}
        </Button>
      </div>

      {report.error ? (
        <ListErrorState
          message={translate("crm.metaOrigin.report.error")}
          onRetry={() => report.refetch()}
        />
      ) : !rangeIsValid ? (
        <ListEmptyState
          className="min-h-0 py-4"
          title={translate("crm.metaOrigin.report.empty")}
        />
      ) : !report.data ? (
        <ListCardsSkeleton rows={3} />
      ) : rows.length === 0 ? (
        <ListEmptyState
          className="min-h-0 py-4"
          title={translate("crm.metaOrigin.report.empty")}
        />
      ) : isMobile ? (
        <ReportCards rows={rows} groupBy={groupBy} />
      ) : (
        <ReportTable rows={rows} groupBy={groupBy} />
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/" />
          <div className="flex min-w-0 flex-1">
            <h1 className="truncate text-xl font-semibold">{title}</h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="space-y-4">{content}</div>
        </MobileContent>
      </>
    );
  }
  return (
    <div className="mx-auto mt-2 max-w-6xl space-y-4 pb-8">
      <h1 className="px-1 text-xl font-semibold">{title}</h1>
      {content}
    </div>
  );
};

type ReportListProps = {
  rows: MetaCampaignReportRow[];
  groupBy: MetaReportGroupBy;
};

const rowKey = (row: MetaCampaignReportRow, index: number) =>
  [row.campaign_id, row.adset_id, row.ad_id, row.platform, index].join("|");

const ReportTable = ({ rows, groupBy }: ReportListProps) => {
  const translate = useTranslate();
  return (
    <Card>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="campaign-report-table">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th scope="col" className="p-3">
                {translate(groupingLabelKey(groupBy))}
              </th>
              <th scope="col" className="p-3">
                {translate("crm.metaOrigin.report.headers.platform")}
              </th>
              {FIGURES.map(({ key }) => (
                <th key={key} scope="col" className="p-3 text-right">
                  {translate(`crm.metaOrigin.report.headers.${key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={rowKey(row, index)} className="border-b last:border-0">
                <th scope="row" className="p-3 text-left font-medium">
                  {rowLabel(row, groupBy)}
                </th>
                <td className="p-3">
                  {row.platform
                    ? translate(`crm.metaOrigin.platform.${row.platform}`)
                    : "-"}
                </td>
                {FIGURES.map(({ key, value }) => (
                  <td key={key} className="p-3 text-right tabular-nums">
                    {value(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

const ReportCards = ({ rows, groupBy }: ReportListProps) => {
  const translate = useTranslate();
  return (
    <ul className="space-y-2" data-testid="campaign-report-cards">
      {rows.map((row, index) => (
        <li key={rowKey(row, index)}>
          <Card>
            <CardContent className="space-y-2 p-4">
              <p className="break-words font-medium">
                {rowLabel(row, groupBy)}
              </p>
              {row.platform ? (
                <p className="text-xs text-muted-foreground">
                  {translate(`crm.metaOrigin.platform.${row.platform}`)}
                </p>
              ) : null}
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                {FIGURES.map(({ key, value }) => (
                  <div key={key}>
                    <dt className="text-xs text-muted-foreground">
                      {translate(`crm.metaOrigin.report.headers.${key}`)}
                    </dt>
                    <dd className="tabular-nums">{value(row)}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
};
